import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import {
  deleteSupportDocForTender,
  subscribeToTenderContextDocs,
  uploadSupportDocForTender,
} from '../../services/tenderFileService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { Tender } from '../../types';
import SharedDropzone from '../common/SharedDropzone';
import type { SupportDoc } from './types';

interface Props {
  tender: Tender;
  onUpdateContext: () => void;
  isPartnerView?: boolean;
}

const UpdateTenderContextCard: React.FC<Props> = ({ tender, onUpdateContext, isPartnerView }) => {
  const theme = useTheme();
  const { account, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();

  const [addedFiles, setAddedFiles] = useState<
    { file: File; status: 'uploading' | 'success' | 'error' }[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [contextFiles, setContextFiles] = useState<SupportDoc[]>([]);

  useEffect(() => {
    if (!tender.id || !account?.uid) {
      return;
    }

    const userTenderPermissions = tender?.memberPermissions?.[account.uid];
    const checkTenderPerm = (perm: string) => {
      if (userTenderPermissions === undefined) {
        return hasPermission ? hasPermission(perm) : false;
      }
      if (Array.isArray(userTenderPermissions)) {
        return userTenderPermissions.includes(perm);
      }
      if (typeof userTenderPermissions === 'object' && userTenderPermissions !== null) {
        return !!(userTenderPermissions as Record<string, boolean>)[perm];
      }
      return false;
    };

    const hasViewAllFiles = Boolean(
      tender.ownerId === account.uid ||
        checkTenderPerm(PERMISSIONS.TENDER_FILES_VIEW_CLIENT) ||
        hasPermission?.(PERMISSIONS.SYSTEM_MANAGE)
    );

    const unsubscribe = subscribeToTenderContextDocs(
      tender.id,
      account.uid,
      hasViewAllFiles,
      (docsData) => {
        setContextFiles(docsData as SupportDoc[]);
      },
      (error: Error) => {
        // biome-ignore lint/suspicious/noConsole: Error logging
        console.error('Firebase snapshot error on tender_docs:', error);
      }
    );

    return () => unsubscribe();
  }, [tender.id, account?.uid, tender.ownerId, tender.memberPermissions, hasPermission]);

  const hasUnupdatedFiles = addedFiles.some((f) => f.status === 'success');

  const handleFilesSelected = async (files: File[]) => {
    if (!account?.uid || !account?.clientId || !account?.actualDefaultBucketName) {
      showSnackbar('Missing account configuration', 'error');
      return;
    }

    if (!tender.id) {
      showSnackbar('Tender ID is missing', 'error');
      return;
    }

    setIsUploading(true);
    // Add files to state initially as uploading
    setAddedFiles((prev) => [
      ...prev,
      ...files.map((file) => ({ file, status: 'uploading' as const })),
    ]);

    const newFilesStatus: { file: File; status: 'uploading' | 'success' | 'error' }[] = [];

    for (const file of files) {
      try {
        await uploadSupportDocForTender({
          userId: account.uid,
          tenderId: tender.id,
          file,
          clientId: account.clientId,
          actualBucketName: account.actualDefaultBucketName,
          docCategory: 'tender_docs',
        });
        newFilesStatus.push({ file, status: 'success' });
      } catch (err: unknown) {
        const error = err as Error;
        showSnackbar(`Failed to upload ${file.name}: ${error.message}`, 'error');
        newFilesStatus.push({ file, status: 'error' });
      }
    }

    setAddedFiles((prev) => {
      const updated = [...prev];
      for (const status of newFilesStatus) {
        const index = updated.findIndex((item) => item.file === status.file);
        if (index !== -1) {
          updated[index] = status;
        }
      }
      return updated;
    });
    setIsUploading(false);
  };

  const truncateFilename = (filename: string) => {
    if (filename.length <= 20) {
      return filename;
    }
    const parts = filename.split('.');
    const ext = parts.pop();
    const name = parts.join('.');
    return `${name.substring(0, 15)}.....${ext}`;
  };

  const handleUpdateClick = () => {
    onUpdateContext();
    // Clear files after update is triggered
    setAddedFiles([]);
  };

  const handleDeleteClick = async (fileItem: {
    file: File;
    status: 'uploading' | 'success' | 'error';
  }) => {
    if (!account?.uid || !account?.clientId || !tender.id) {
      return;
    }

    if (fileItem.status === 'success') {
      try {
        await deleteSupportDocForTender({
          userId: account.uid,
          clientId: account.clientId,
          tenderId: tender.id,
          fileName: fileItem.file.name,
          docCategory: 'tender_docs',
        });
      } catch (err: unknown) {
        const error = err as Error;
        showSnackbar(`Failed to delete ${fileItem.file.name}: ${error.message}`, 'error');
      }
    }

    setAddedFiles((prev) => prev.filter((item) => item.file !== fileItem.file));
  };

  const handleDeleteContextFile = async (fileName: string) => {
    if (!account?.uid || !account?.clientId || !tender.id) {
      return;
    }

    try {
      await deleteSupportDocForTender({
        userId: account.uid,
        clientId: account.clientId,
        tenderId: tender.id,
        fileName: fileName,
        docCategory: 'tender_docs',
      });
    } catch (err: unknown) {
      const error = err as Error;
      showSnackbar(`Failed to delete ${fileName}: ${error.message}`, 'error');
    }
  };

  const displayContextFiles = contextFiles.filter(
    (cf) =>
      !addedFiles.some((af) => af.file.name === cf.originalFilename || af.file.name === cf.name) &&
      !cf.originalFilename?.includes('notice_only') &&
      !cf.name?.includes('notice_only')
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${hasUnupdatedFiles ? theme.palette.error.main : theme.palette.divider}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
      }}
    >
      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
        {isPartnerView ? 'Tender Context Documents' : 'Update Tender Context'}
      </Typography>

      {!isPartnerView && (
        <>
          <Typography
            variant="body2"
            color={hasUnupdatedFiles ? 'error.main' : 'text.secondary'}
            sx={{ mb: 1 }}
          >
            {hasUnupdatedFiles
              ? "Click 'Update Context' to recalibrate."
              : 'Context up to date. Continue steps and generate draft.'}
          </Typography>

          <SharedDropzone
            onFilesSelected={handleFilesSelected}
            dropzoneText="Drop files to update context"
            type="tender_docs"
            isLoading={isUploading}
          />
        </>
      )}

      {(addedFiles.length > 0 || displayContextFiles.length > 0) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {addedFiles.map((item) => (
            <Box
              key={item.file.name}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '80%' }}>
                <Tooltip
                  title={
                    item.status === 'success'
                      ? 'Tender context has not been fully updated'
                      : item.status === 'uploading'
                        ? 'Uploading...'
                        : 'Upload failed'
                  }
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.status === 'success' ? (
                      <CancelIcon color="error" fontSize="small" />
                    ) : item.status === 'uploading' ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </Box>
                </Tooltip>
                <Typography variant="body2" noWrap>
                  {truncateFilename(item.file.name)}
                </Typography>
              </Box>
              {!isPartnerView && (
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(item)}
                  disabled={item.status === 'uploading'}
                  color="default"
                >
                  <DeleteIcon sx={{ fontSize: 28 }} />
                </IconButton>
              )}
            </Box>
          ))}
          {displayContextFiles.map((doc) => {
            const fileName = doc.originalFilename || doc.name || doc.id || 'Unknown File';
            const status = doc.conversionStatus || 'success';

            let statusIcon: React.ReactNode = null;
            let statusTooltip = 'In context';

            if (status === 'processing' || status === 'pending') {
              statusIcon = <CircularProgress size={16} />;
              statusTooltip = 'Processing...';
            } else if (status === 'failed' || status === 'missing_file') {
              statusIcon = <ErrorIcon color="error" fontSize="small" />;
              statusTooltip =
                status === 'missing_file'
                  ? 'Delete and reupload (File Missing)'
                  : 'Processing failed';
            } else {
              statusIcon = <CheckCircleIcon color="success" fontSize="small" />;
            }

            return (
              <Box
                key={fileName}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '80%' }}>
                  <Tooltip title={statusTooltip}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>{statusIcon}</Box>
                  </Tooltip>
                  <Typography variant="body2" noWrap>
                    {truncateFilename(fileName)}
                  </Typography>
                </Box>
                {!isPartnerView && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteContextFile(fileName)}
                    color="default"
                  >
                    <DeleteIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {!isPartnerView && (
        <Button
          variant="contained"
          disabled={!hasUnupdatedFiles || isUploading}
          onClick={handleUpdateClick}
          sx={{ mt: 'auto' }}
        >
          Update Context
        </Button>
      )}
    </Paper>
  );
};

export default UpdateTenderContextCard;
